import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { FEEDBACK_KINDS, FEEDBACK_KIND_LABELS, validateFeedback } from '@cerebro-diem/core';
import type { FeedbackRow } from '@cerebro-diem/core';
import { APP_VERSION } from '../services/feedback';
import { errorMessage } from '../services/errors';
import { queryKeys } from '../services/queries';
import { useFeedbackDialog } from '../stores/feedbackStore';
import DictationTextarea from './DictationTextarea';
import { dialogBackdrop, dialogPanel, iconButton, outlineButton, primaryButton, tapLink, tapTarget } from './styles';

/**
 * A short form for a bug, a suggestion or feedback. The screen it was opened
 * from, the app version and the platform are attached and shown; nothing else
 * is. Send commits the draft to durable local storage before clearing the form.
 * A waiting report survives restart and can be retried from Your feedback. "Your feedback"
 * leads to everything already sent and where each stands.
 */
export default function FeedbackDialog({ screen, onClose }: { screen: string; onClose: () => void }) {
  const { kind, message, setKind, setMessage, enqueue, retry, pending, storageError, sendError, ready, retrySavingDraft, ownerId } = useFeedbackDialog();
  const draftRecovery = useMutation({ mutationFn: retrySavingDraft });
  const [invalid, setInvalid] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (row: FeedbackRow) => {
      const owner = useFeedbackDialog.getState().ownerId;
      const id = await enqueue(row);
      if (useFeedbackDialog.getState().ownerId !== owner) throw new Error('The signed-in account changed; your report remains with its author');
      await retry();
      if (useFeedbackDialog.getState().ownerId !== owner) throw new Error('The signed-in account changed; your report remains with its author');
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.feedback, owner] });
      return { id, owner };
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !mutation.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mutation.isPending, onClose]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validateFeedback({ kind, message, screen, appVersion: APP_VERSION, platform: 'web' });
    if (!result.ok) {
      setInvalid(`${result.error}.`);
      return;
    }
    setInvalid(null);
    mutation.mutate(result.row);
  };

  const yourFeedback = (
    <Link to="/feedback" onClick={onClose} className={`${tapLink} text-sm text-primary underline`}>
      Your feedback
    </Link>
  );

  return (
    <div className={dialogBackdrop} onClick={() => !mutation.isPending && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        className={dialogPanel}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="feedback-title" className="text-lg font-semibold">
            Send feedback
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            aria-label="Close"
            className={`${iconButton} -mr-2 p-1 rounded-lg hover:bg-surface-variant`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {mutation.isSuccess && mutation.data.owner === ownerId ? (
          <div className="space-y-4">
            <p role="status" className="p-3 rounded-xl bg-success-container text-on-success-container">
              {pending.some(item => item.id === mutation.data.id) ? 'Waiting to send. Kept on this device; Your feedback shows waiting reports and Retry.' : 'Saved. Thank you: the team reads every one, and Your feedback shows what happens to it.'}
            </p>
            {sendError && <p role="alert" className="text-sm text-error">{sendError}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={onClose} className={primaryButton}>
                Done
              </button>
              <button type="button" onClick={() => mutation.reset()} className={outlineButton}>
                Send another
              </button>
              {yourFeedback}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <fieldset className="flex flex-wrap gap-2">
              <legend className="sr-only">Type</legend>
              {FEEDBACK_KINDS.map((option) => (
                <label
                  key={option}
                  className={`inline-flex items-center ${tapTarget} px-3 py-1.5 rounded-xl text-sm font-medium border cursor-pointer ${
                    kind === option ? 'bg-primary-container text-primary border-primary' : 'border-outline/30 hover:bg-surface-variant'
                  }`}
                >
                  <input type="radio" name="feedback-kind" value={option} checked={kind === option} onChange={() => setKind(option)} className="sr-only" />
                  {FEEDBACK_KIND_LABELS[option]}
                </label>
              ))}
            </fieldset>
            <DictationTextarea
              label="What happened, or what would help?"
              rows={5}
              autoFocus
              value={message}
              onChange={(next) => {
                setMessage(next);
                setInvalid(null);
              }}
            />
            <p className="text-xs text-on-surface-variant">
              Sent with it: screen {screen}, version {APP_VERSION}, web. No screenshot and nothing from your notes or tasks.
            </p>
            {storageError && <div><p role="alert" className="text-sm text-error">{storageError}</p><p className="text-sm text-error">Your latest input is not saved on this device. Copy it before closing or restarting.</p><button type="button" className={outlineButton} disabled={draftRecovery.isPending} onClick={() => draftRecovery.mutate()}>Retry saving draft</button></div>}
            {invalid && <p className="text-sm text-error">{invalid}</p>}
            {mutation.isError && (
              <p role="alert" className="text-sm text-error">
                Not queued: {errorMessage(mutation.error)}. Your text is still here.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button type="submit" disabled={mutation.isPending || !ready || !!storageError} className={primaryButton}>
                {mutation.isPending ? 'Sending…' : 'Send'}
              </button>
              <button type="button" onClick={onClose} disabled={mutation.isPending} className={outlineButton}>
                Cancel
              </button>
              {yourFeedback}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
